// src/components/FlowCanvas.tsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import CategoryNode from "./CategoryNode";
import {
  // collections,
  // kpis,
  // systems,
  // categories,
  useFetchSystem,
} from "./sampledata";
import {
  DataSecurityType,
  EffectivenessType,
  InformationCollectionToolType,
} from "../../utils/types";

const nodeTypes = {
  category: CategoryNode,
};

// Define layer positions - revised for 3 clear sections
const LAYER_SPACING = 250;
const KPI_LAYER_Y = 80;
const SYSTEM_LAYER_Y = KPI_LAYER_Y + LAYER_SPACING;
const COLLECTION_LAYER_Y = SYSTEM_LAYER_Y + LAYER_SPACING;
const DEPARTMENT_LAYER_Y = COLLECTION_LAYER_Y + LAYER_SPACING;

export function getContrastingTextColor(bgColor: string): string {
  // Handle undefined, null, or empty color values
  if (!bgColor || typeof bgColor !== "string") {
    return "#000"; // Default to black text
  }

  const hex = bgColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#000" : "#fff";
}

const tailwindColorMap: Record<string, string> = {
  "bg-blue-200": "#BFDBFE",
  "bg-red-200": "#F57B42",
  // "bg-red-200": "#FECACA",
  "bg-yellow-200": "#FEF08A",
  "bg-purple-200": "#E9D5FF",
  "bg-green-200": "#BBF7D0",
  "bg-orange-200": "#FED7AA",
  "bg-pink-200": "#FBCFE8",
};
export function tailwindToHex(twClass: string): string {
  return tailwindColorMap[twClass] ?? "#ccc";
}

const typeColorMap: Record<InformationCollectionToolType, string> = {
  "Paper Based": "#1E3A8A", // Strong Blue
  "Paper Based with Digital Integration": "#B91C1C", // Strong Red
  "In-House Digital Tool": "#CA8A04", // Strong Yellow-Gold
  "Open Digital Tool": "#7E22CE", // Strong Purple
  "Proprietary Digital Tool": "#15803D", // Strong Green
  "Mobile App": "#C2410C", // Strong Orange
  "Web Based Survey": "#BE185D", // Strong Pink
  "Sensors and IoT Devices": "#0F172A", // Dark Navy
  "Don’t Know": "#525252", // Solid Gray
};

export function typeToHex(twClass: InformationCollectionToolType): string {
  return typeColorMap[twClass] ?? "#ccc";
}

const effectivenessColorMap: Record<
  EffectivenessType | DataSecurityType,
  string
> = {
  "Don’t Know": "#525252", // Solid Gray
  Ineffective: "#B91C1C", // Strong Red
  Basic: "#DC2626", // Vivid Red
  "Moderately Effective": "#D97706", // Amber/Orange
  Minimal: "#CA8A04", // Golden Yellow
  Effective: "#15803D", // Strong Green
  Advanced: "#166534", // Deeper Green
};

export function effectivenessToHex(
  twClass: EffectivenessType | DataSecurityType
): string {
  return effectivenessColorMap[twClass] ?? "#ccc";
}

// ========== COLOR MANAGEMENT IMPROVEMENTS ==========

// Extended color palette for handling many categories
const EXTENDED_COLOR_PALETTE = [
  "#1E3A8A", // Strong Blue
  "#B91C1C", // Strong Red
  "#CA8A04", // Golden Yellow
  "#7E22CE", // Strong Purple
  "#15803D", // Strong Green
  "#C2410C", // Strong Orange
  "#BE185D", // Strong Pink
  "#0F172A", // Dark Navy
  "#0891B2", // Cyan
  "#7C2D12", // Brown
  "#1E40AF", // Blue
  "#DC2626", // Vivid Red
  "#D97706", // Amber
  "#059669", // Emerald
  "#9333EA", // Violet
  "#EA580C", // Orange
  "#EC4899", // Pink
  "#6366F1", // Indigo
  "#14B8A6", // Teal
  "#F59E0B", // Yellow
  "#EF4444", // Red
  "#10B981", // Green
  "#8B5CF6", // Purple
  "#F97316", // Orange
  "#06B6D4", // Cyan
];

// Color cache to avoid repeated calculations
const colorCache = new Map<string, string>();
const colorConflictCache = new Map<string, Set<string>>();

/**
 * Calculate color distance using Euclidean distance in RGB space
 * Returns a value between 0 (identical) and ~441 (maximum difference)
 */
function getColorDistance(color1: string, color2: string): number {
  const hex1 = color1.replace("#", "");
  const hex2 = color2.replace("#", "");

  const r1 = parseInt(hex1.substring(0, 2), 16);
  const g1 = parseInt(hex1.substring(2, 4), 16);
  const b1 = parseInt(hex1.substring(4, 6), 16);

  const r2 = parseInt(hex2.substring(0, 2), 16);
  const g2 = parseInt(hex2.substring(2, 4), 16);
  const b2 = parseInt(hex2.substring(4, 6), 16);

  const distance = Math.sqrt(
    Math.pow(r2 - r1, 2) + Math.pow(g2 - g1, 2) + Math.pow(b2 - b1, 2)
  );

  return distance;
}

/**
 * Check if two colors are too similar (conflict detection)
 * Threshold: 50 (adjustable based on needs)
 */
function areColorsSimilar(
  color1: string,
  color2: string,
  threshold = 50
): boolean {
  if (!color1 || !color2) return false;
  const distance = getColorDistance(color1, color2);
  return distance < threshold;
}

/**
 * Generate a color from the extended palette with rotation
 * Ensures colors are reused when there are more categories than colors
 */
function generateColorFromPalette(index: number): string {
  const paletteIndex = index % EXTENDED_COLOR_PALETTE.length;
  return EXTENDED_COLOR_PALETTE[paletteIndex];
}

/**
 * Assign colors to categories with conflict detection and rotation
 * Returns a map of category ID to assigned color
 */
function assignColorsToCategories(
  categories: Array<{ id: string; name: string; color?: string }>
): Map<string, string> {
  const colorMap = new Map<string, string>();
  const usedColors = new Set<string>();

  categories.forEach((category, index) => {
    // If category already has a color, check for conflicts
    if (category.color) {
      let hasConflict = false;

      // Check if this color conflicts with already assigned colors
      const colorMapEntries = Array.from(colorMap.entries());
      for (const [catId, assignedColor] of colorMapEntries) {
        if (areColorsSimilar(category.color, assignedColor)) {
          hasConflict = true;
          break;
        }
      }

      if (!hasConflict && !usedColors.has(category.color)) {
        colorMap.set(category.id, category.color);
        usedColors.add(category.color);
        return;
      }
    }

    // Generate a new color from palette
    let attempts = 0;
    let assignedColor: string;

    do {
      assignedColor = generateColorFromPalette(index + attempts);
      attempts++;

      // Prevent infinite loop
      if (attempts > EXTENDED_COLOR_PALETTE.length * 2) {
        assignedColor = generateColorFromPalette(index);
        break;
      }
    } while (
      usedColors.has(assignedColor) &&
      attempts < EXTENDED_COLOR_PALETTE.length
    );

    // If we've used all colors, allow reuse but try to maximize distance
    if (usedColors.has(assignedColor)) {
      // Find the color that's least similar to recently used colors
      let bestColor = assignedColor;
      let maxMinDistance = 0;

      for (const paletteColor of EXTENDED_COLOR_PALETTE) {
        let minDistance = Infinity;
        const usedColorsArray = Array.from(usedColors);
        for (const usedColor of usedColorsArray) {
          const distance = getColorDistance(paletteColor, usedColor);
          minDistance = Math.min(minDistance, distance);
        }

        if (minDistance > maxMinDistance) {
          maxMinDistance = minDistance;
          bestColor = paletteColor;
        }
      }

      assignedColor = bestColor;
    }

    colorMap.set(category.id, assignedColor);
    usedColors.add(assignedColor);
  });

  return colorMap;
}

/**
 * Get color for a category with caching
 */
function getCategoryColor(
  categoryId: string,
  categories: Array<{ id: string; name: string; color?: string }>,
  colorAssignmentMap: Map<string, string>,
  fallbackIndex: number = 0
): string {
  const cacheKey = `cat-${categoryId}-${fallbackIndex}`;

  if (colorCache.has(cacheKey)) {
    return colorCache.get(cacheKey)!;
  }

  const category = categories.find((c) => c.id === categoryId);
  let color: string;

  if (category) {
    // Use assigned color from map, or fallback to category's color, or generate
    color =
      colorAssignmentMap.get(categoryId) ||
      category.color ||
      generateColorFromPalette(fallbackIndex);
  } else {
    // Generate a color from palette instead of grey
    color = generateColorFromPalette(fallbackIndex);
  }

  colorCache.set(cacheKey, color);
  return color;
}

/**
 * Clear color cache (useful when data changes)
 */
function clearColorCache(): void {
  colorCache.clear();
  colorConflictCache.clear();
}

function getCenteredX(
  index: number,
  total: number,
  nodeWidth = 200,
  spacing = 50,
  containerWidth = 1200
) {
  const totalWidth = total * nodeWidth + (total - 1) * spacing;
  const startX = Math.max((containerWidth - totalWidth) / 2, 0); // prevent negative
  return startX + index * (nodeWidth + spacing);
}

const FlowCanvas: React.FC<{
  usecase?: "type" | "effectiveness";
  onClick?: (d?: string) => void;
  orgData?: string;
}> = ({ usecase, onClick, orgData }) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  );
  const [activeSystemId, setActiveSystemId] = useState<string | null>(null),
    { systems, categories, kpis, collections } = useFetchSystem({
      organization: orgData,
    });

  const filteredSystems = systems.filter(
    (s) =>
      // selectedCategoryId ? s?.categories?.includes(selectedCategoryId) :
      true
  );

  const visibleKPIs = kpis.filter((kpi) =>
    // activeSystemId
    // 	? kpi?.systemIds.includes(activeSystemId)
    // 	:
    kpi?.systemIds.some((id) => filteredSystems.map((s) => s?.id).includes(id))
  );

  // const visibleKPIs = kpis.filter(kpi => {
  // 	if (activeSystemId) {
  // 		return kpi.systemIds.includes(activeSystemId);
  // 	} else if (selectedCategoryId) {
  // 		const systemIdsInCategory = systems
  // 			.filter(sys => sys.categories?.includes(selectedCategoryId))
  // 			.map(sys => sys.id);

  // 		return kpi.systemIds.some(id => systemIdsInCategory.includes(id));
  // 	}

  // 	return false; // No selection = hide all KPIs
  // });

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const nodes: Node[] = useMemo(() => {
    // Clear cache when dependencies change
    clearColorCache();

    // Assign colors to all categories with conflict detection and rotation
    const colorAssignmentMap = assignColorsToCategories(categories);

    const nodes: Node[] = [
      {
        id: "kpi-label-left",
        type: "category",
        position: { x: 0, y: KPI_LAYER_Y },
        data: {
          label: "KPIs",
          background: "#e0e0e0",
          orientation: "vertical",
          isLabel: true,
        },
      },
      {
        id: "systems-label-left",
        type: "category",
        position: { x: 0, y: SYSTEM_LAYER_Y },
        data: {
          label: "Data Systems",
          background: "#e0e0e0",
          orientation: "vertical",
          isLabel: true,
        },
      },
      {
        id: "collections-label-left",
        type: "category",
        position: { x: 0, y: COLLECTION_LAYER_Y },
        data: {
          label: "Data Collections",
          background: "#e0e0e0",
          orientation: "vertical",
          isLabel: true,
        },
      },
      {
        id: "departments-label-left",
        type: "category",
        position: { x: 0, y: DEPARTMENT_LAYER_Y },
        data: {
          label: "Departments",
          background: "#e0e0e0",
          orientation: "vertical",
          isLabel: true,
        },
      },
    ];

    // CATEGORY nodes (bottom)
    categories.forEach((cat, i) => {
      const categoryColor = getCategoryColor(
        cat.id,
        categories,
        colorAssignmentMap,
        i
      );
      const textColor = getContrastingTextColor(categoryColor);

      nodes.push({
        id: `cat-${cat?.id}`,
        position: {
          x: getCenteredX(i, categories.length, 180, 50, containerWidth) + 50,
          y: DEPARTMENT_LAYER_Y,
        },
        data: {
          label: cat?.name,
          color: categoryColor,
          opacity:
            selectedCategoryId && selectedCategoryId !== cat?.id ? 0.2 : 1,
          boxType: "department",
        },
        type: "category",
        style: {
          background: categoryColor,
          color: textColor,
          fontWeight: "bold",
          padding: 10,
          borderRadius: 8,
          minWidth: 120,
          textAlign: "center",
          opacity:
            selectedCategoryId && selectedCategoryId !== cat?.id ? 0.2 : 1,
          cursor: "pointer",
        },
      });
    });

    // COLLECTION node (lower middle)
    collections.forEach((collection, i) => {
      // Determine opacity based on selection

      let isFaded = false;

      if (selectedCategoryId) {
        // Fade collections NOT connected to the selected category
        isFaded = !collection.categories.includes(selectedCategoryId);
      } else if (activeSystemId) {
        // When system selected, collections not faded
        isFaded = false;
      }

      const opacity = isFaded ? 0.2 : 1;

      // Color logic: if category selected and collection has that category, use category color, else collection.color
      let color = collection.color;
      if (
        selectedCategoryId
        // &&
        // collection.categories.includes(selectedCategoryId)
      ) {
        color = getCategoryColor(
          selectedCategoryId,
          categories,
          colorAssignmentMap,
          i
        );
      } else {
        const firstCategoryId = collection?.categories?.[0];
        if (firstCategoryId) {
          const catIndex = collection.categories.indexOf(firstCategoryId);
          color = getCategoryColor(
            firstCategoryId,
            categories,
            colorAssignmentMap,
            catIndex
          );
        } else {
          color = collection.color || generateColorFromPalette(i);
        }
      }

      const textColor = getContrastingTextColor(color);

      nodes.push({
        id: collection.id,
        position: {
          x: getCenteredX(i, collections.length, 220, 50, containerWidth) + 50,
          y: COLLECTION_LAYER_Y,
        }, // example position, adjust as needed
        type: "category", // define this node type in your React Flow setup
        data: {
          label: collection.label,
          color,
          opacity,
          boxType: "collection",
        },
        style: {
          backgroundColor: color,
          color: textColor,
          opacity,
          padding: 10,
          borderRadius: 10,
          minWidth: 160,
          textAlign: "center",
          fontWeight: "medium",
        },
      });
    });

    // SYSTEM nodes (middle)
    filteredSystems.forEach((sys, i) => {
      const matchedCats = sys.categories
        .map((catId) => categories.find((c) => c.id === catId))
        .filter(Boolean);

      // const primaryColor = matchedCats[0]?.color || "#f5f5f5";
      // const textColor = getContrastingTextColor(primaryColor);

      const prioritizedCat = selectedCategoryId
        ? matchedCats.find((cat) => cat?.id === selectedCategoryId) ??
          matchedCats[0]
        : matchedCats[0];

      const primaryColor = prioritizedCat
        ? getCategoryColor(prioritizedCat.id, categories, colorAssignmentMap, i)
        : generateColorFromPalette(i);
      const textColor = getContrastingTextColor(primaryColor);

      let border = usecase
        ? `7px dashed ${
            usecase === "effectiveness"
              ? `${effectivenessToHex(sys?.effectiveness)}`
              : usecase === "type"
              ? `${typeToHex(sys?.type)}`
              : ""
          }`
        : "";

      if (usecase)
        console.log({
          border,
          usecase,
          effectColor: effectivenessToHex(sys?.effectiveness),
          effect: sys?.effectiveness,
        });

      nodes.push({
        id: sys.id,
        position: {
          x:
            getCenteredX(i, filteredSystems.length, 200, 50, containerWidth) +
            50,
          y: SYSTEM_LAYER_Y,
        },
        data: {
          label: sys.label,
          // label: (
          // 	<div>
          // 		<div className="font-bold">{sys.label}</div>
          // 		<div className="flex flex-wrap justify-center gap-1 mt-1">
          // 			{matchedCats.map(cat => (
          // 				<span
          // 					key={cat?.id}
          // 					className={`text-xs px-2 py-0.5 rounded-full ${
          // 						cat?.color
          // 					} ${getContrastingTextColor(cat?.color)}`}>
          // 					{cat?.name}
          // 				</span>
          // 			))}
          // 		</div>
          // 	</div>
          // ),
          color: primaryColor,
          opacity:
            selectedCategoryId && !sys.categories.includes(selectedCategoryId)
              ? 0.2
              : 1,
          onInfoClick: () => {
            onClick && onClick(sys?.id);
          },
          border,
        },
        type: "category",
        style: {
          background: primaryColor,
          color: textColor,
          fontWeight: "bold",
          padding: 10,
          borderRadius: 10,
          minWidth: 160,
          textAlign: "center",
          cursor: "pointer",
          opacity: activeSystemId && activeSystemId !== sys.id ? 0.2 : 1,
        },
      });
    });

    // KPI nodes (top)
    visibleKPIs.forEach((kpi, i) => {
      // const catSystem = systems.find(s => s?.id === kpi.systemIds?.[0]);
      // const categoryMatch = categories.find(c =>
      // 	catSystem?.categories?.includes(c?.id)
      // );
      let catColor: any = null;

      if (kpi.systemIds?.length > 0) {
        let primarySystem = systems.find((s) => s.id === kpi.systemIds[0]);

        if (selectedCategoryId) {
          // If a category is selected, prioritize it
          catColor = categories.find((c) => c.id === selectedCategoryId);
        } else if (activeSystemId) {
          // If a system is selected, prioritize its first category
          const activeSystem = systems.find((s) => s.id === activeSystemId);
          if (activeSystem?.categories?.length > 0) {
            catColor = categories.find(
              (c) => c.id === activeSystem.categories[0]
            );
          }
        } else if (primarySystem) {
          // Fallback to default primary system category
          catColor = categories.find((c) =>
            primarySystem.categories.includes(c.id)
          );
        }
      }

      const color = catColor
        ? getCategoryColor(catColor.id, categories, colorAssignmentMap, i)
        : generateColorFromPalette(i);
      const textColor = getContrastingTextColor(color);

      // Determine if this KPI is "connected"
      let isFaded = false;

      if (selectedCategoryId) {
        const connectedSystems = systems.filter((s) =>
          s.categories.includes(selectedCategoryId)
        );
        const connectedSystemIds = connectedSystems.map((s) => s.id);
        isFaded = !kpi.systemIds.some((id) => connectedSystemIds.includes(id));
      } else if (activeSystemId) {
        isFaded = !kpi.systemIds.includes(activeSystemId);
      }

      nodes.push({
        id: kpi?.id,
        position: {
          x: getCenteredX(i, visibleKPIs.length, 250, 50, containerWidth) + 50,
          y: KPI_LAYER_Y,
        },
        type: "category",
        data: {
          label: kpi?.label,
          color,
          opacity: isFaded ? 0.2 : 1,
        },
        style: {
          color: textColor,
          background: color,
          padding: 10,
          borderRadius: 10,
          minWidth: 160,
          textAlign: "center",
          fontWeight: "medium",
          opacity: isFaded ? 0.2 : 1,
        },
      });
    });
    return nodes;
  }, [
    collections,
    systems,
    categories,
    kpis,
    selectedCategoryId,
    activeSystemId,
    usecase,
    containerWidth,
  ]);

  // EDGES
  const edges: Edge[] = useMemo(() => {
    // Get color assignment map for edges (reuse from nodes calculation)
    const colorAssignmentMap = assignColorsToCategories(categories);

    const edges: Edge[] = [];
    const edgeSet = new Set<string>();
    filteredSystems.forEach((sys) => {
      sys.categories.forEach((catId) => {
        const isFaded =
          (selectedCategoryId && catId !== selectedCategoryId) ||
          (activeSystemId && sys.id !== activeSystemId);

        let catColor: string | null = null;

        if (selectedCategoryId && sys.categories.includes(selectedCategoryId)) {
          const catIndex = sys.categories.indexOf(selectedCategoryId);
          catColor = getCategoryColor(
            selectedCategoryId,
            categories,
            colorAssignmentMap,
            catIndex
          );
        } else {
          const matchingCat = categories.find((c) =>
            sys.categories.includes(c.id)
          );
          if (matchingCat) {
            const catIndex = sys.categories.indexOf(matchingCat.id);
            catColor = getCategoryColor(
              matchingCat.id,
              categories,
              colorAssignmentMap,
              catIndex
            );
          }
        }

        // If no category found, generate a color based on system index
        if (!catColor) {
          const sysIndex = filteredSystems.findIndex((s) => s.id === sys.id);
          catColor = generateColorFromPalette(sysIndex);
        }

        const strokeColor = tailwindToHex(catColor);
        const edgeId = `edge-cat-${catId}->${sys.id}`;
        if (!edgeSet.has(edgeId)) {
          edgeSet.add(edgeId);
          edges.push({
            id: edgeId,
            source: `cat-${catId}`,
            target: sys.id,
            animated: false,
            type: "smoothstep",
            style: {
              strokeWidth: 3,
              stroke: strokeColor,
              opacity: isFaded ? 0.2 : 1,
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: strokeColor,
            },
          });
        }
      });
    });

    visibleKPIs.forEach((kpi) => {
      kpi.systemIds.forEach((sysId) => {
        if (filteredSystems) {
          // const catSystem = systems.find(c => c?.id === kpi?.systemIds?.[0]);
          // const catColor = categories.find(c =>
          // 	catSystem?.categories?.includes(c.id)
          // )?.color;

          const system = systems.find((s) => s.id === sysId);
          if (!system) return;

          let catColor: string | null = null;

          if (
            selectedCategoryId &&
            system.categories.includes(selectedCategoryId)
          ) {
            // If a category is selected and the system belongs to it
            const catIndex = system.categories.indexOf(selectedCategoryId);
            catColor = getCategoryColor(
              selectedCategoryId,
              categories,
              colorAssignmentMap,
              catIndex
            );
          } else if (!selectedCategoryId && activeSystemId === system.id) {
            // If a system is selected and no category is selected, use its first category
            const primaryCatId = system.categories?.[0];
            if (primaryCatId) {
              catColor = getCategoryColor(
                primaryCatId,
                categories,
                colorAssignmentMap,
                0
              );
            }
          } else {
            // Fallback to any matching category
            const matchingCat = categories.find((c) =>
              system.categories.includes(c.id)
            );
            if (matchingCat) {
              const catIndex = system.categories.indexOf(matchingCat.id);
              catColor = getCategoryColor(
                matchingCat.id,
                categories,
                colorAssignmentMap,
                catIndex
              );
            }
          }

          // If no category found, generate a color based on system index
          if (!catColor) {
            const sysIndex = systems.findIndex((s) => s.id === sysId);
            catColor = generateColorFromPalette(sysIndex);
          }

          const strokeColor = tailwindToHex(catColor);

          const isFaded =
            (selectedCategoryId &&
              !system.categories.includes(selectedCategoryId)) ||
            (activeSystemId && sysId !== activeSystemId);
          const sourceNode = nodes.find((n) => n.id === sysId);
          const kpiNode = nodes.find((n) => n.id === kpi.id);

          if (sourceNode && kpiNode) {
            const edgeId = `edge-sys-${sysId}->kpi-${kpi.id}`;
            if (!edgeSet.has(edgeId)) {
              edgeSet.add(edgeId);
              edges.push({
                id: edgeId,
                source: sysId,
                target: kpi?.id,
                animated: false,
                type: "smoothstep",
                style: {
                  strokeWidth: 3,
                  stroke: strokeColor,
                  opacity: isFaded ? 0.2 : 1,
                },
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  color: strokeColor,
                },
              });
            }
          }
        }
      });
    });

    collections.forEach((collection) => {
      // Edges from categories to collection
      collection.categories.forEach((catId) => {
        const edgeId = `cat-${catId}-to-collection-${collection.id}`;

        // Fade edge when category selected and this edge not connected
        let opacity = 1;
        if (selectedCategoryId) {
          opacity = catId === selectedCategoryId ? 1 : 0.2;
        }
        const isFaded = selectedCategoryId && catId !== selectedCategoryId;

        let catColor: string | null = null;

        if (
          selectedCategoryId &&
          collection.categories.includes(selectedCategoryId)
        ) {
          const catIndex = collection.categories.indexOf(selectedCategoryId);
          catColor = getCategoryColor(
            selectedCategoryId,
            categories,
            colorAssignmentMap,
            catIndex
          );
        } else {
          const matchingCat = categories.find((c) =>
            collection.categories.includes(c.id)
          );
          if (matchingCat) {
            const catIndex = collection.categories.indexOf(matchingCat.id);
            catColor = getCategoryColor(
              matchingCat.id,
              categories,
              colorAssignmentMap,
              catIndex
            );
          }
        }

        // If no category found, use collection color or generate from palette
        if (!catColor) {
          if (collection.color) {
            catColor = collection.color;
          } else {
            const collectionIndex = collections.findIndex(
              (c) => c.id === collection.id
            );
            catColor = generateColorFromPalette(collectionIndex);
          }
        }

        const strokeColor = tailwindToHex(catColor);

        // System selection does NOT fade collection edges
        if (!edgeSet.has(edgeId)) {
          edgeSet.add(edgeId);
          edges.push({
            id: edgeId,
            source: `cat-${catId}`,
            target: collection.id,
            animated: false,
            type: "smoothstep",
            style: {
              stroke: strokeColor,
              strokeWidth: 2,
              opacity,
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: strokeColor,
            },
            // arrowHeadType: "arrowclosed",
          });
        }
      });

      // Edges from collection to systems
      collection.systemIds.forEach((sysId) => {
        const edgeId = `collection-${collection.id}-to-system-${sysId}`;

        // Fade edge based on category or system selection
        let opacity = 1;

        if (selectedCategoryId) {
          // Fade if collection or system NOT connected to selectedCategoryId
          const system = systems.find((s) => s.id === sysId);
          const systemHasCategory =
            system?.categories.includes(selectedCategoryId);
          const collectionHasCategory =
            collection.categories.includes(selectedCategoryId);
          opacity = systemHasCategory && collectionHasCategory ? 1 : 0.2;
        } else if (activeSystemId) {
          // Fade if edge not connected to active system
          opacity = sysId === activeSystemId ? 1 : 0.2;
        }

        // Use collection color for stroke or generate from palette
        const collectionIndex = collections.findIndex(
          (c) => c.id === collection.id
        );
        const color =
          collection.color || generateColorFromPalette(collectionIndex);
        if (!edgeSet.has(edgeId)) {
          edgeSet.add(edgeId);
          edges.push({
            id: edgeId,
            source: collection.id,
            target: sysId,
            animated: false,
            type: "smoothstep",
            style: {
              stroke: color,
              strokeWidth: 2,
              opacity,
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color,
            },
            // arrowHeadType: "arrowclosed",
          });
        }
      });
    });
    return edges;
  }, [
    collections,
    systems,
    categories,
    kpis,
    selectedCategoryId,
    activeSystemId,
    usecase,
  ]);

  // const [nodesState, setNodesState, onNodesChange] = useNodesState(nodes);
  // const [edgesState, setEdgesState, onEdgesChange] = useEdgesState(edges);

  // // Optionally sync states when nodes or edges update
  // useEffect(() => {
  // 	setNodesState(nodes);
  // 	setEdgesState(edges);
  // }, [nodes, edges, usecase]);

  // Handle node clicks
  const handleNodeClick = (_: any, node: Node) => {
    const isCategoryNode = node.id.startsWith("cat-");
    const catId = isCategoryNode ? node.id.replace("cat-", "") : null;

    if (isCategoryNode) {
      setSelectedCategoryId((prev) => (prev === catId ? null : catId));
      setActiveSystemId(null);
    } else if (systems.find((s) => s?.id === node?.id)) {
      setActiveSystemId((prev) => (prev === node?.id ? null : node?.id));
      setSelectedCategoryId(null);
    }
  };

  if (!nodes.length || !edges.length) return <div>Loading...</div>;

  return (
    <div className="w-full h-[650px] border rounded" ref={containerRef}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        // onNodesChange={onNodesChange}
        // onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={{
          type: "smoothstep", // or "straight" "step"
          animated: false,
        }}
        fitView
        zoomOnScroll={false} // disables scroll zoom
      >
        <Background />
        <Controls />
      </ReactFlow>
    </div>
  );
};

export default FlowCanvas;
